/* ==========================================================================
   Incipit · Featured Products — v3
   • Tabs : changement de panneau actif
   • Slider : SCROLL NATIF horizontal (swipe parfait sur mobile)
   • Quick add : Incipit Quick View drawer pour multi-variantes
   • Ajout final : event cart:update → drawer panier Atelier auto-open
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- Helpers ------------------------------------------------------ */

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const formatMoney = (cents) => {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return amount + ' €';
  };

  const escapeHtml = (str) => {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const ATELIER_CART_EVENT = 'cart:update'; // matches ThemeEvents.cartUpdate in Atelier


  /* ---------- TABS --------------------------------------------------------- */

  class IncipitFPTabs {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.tabs = section.querySelectorAll('[data-tab-target]');
      this.panels = section.querySelectorAll('[data-tab-panel]');
      if (this.tabs.length === 0) return;
      this.bind();
    }

    bind() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.tabTarget));
      });
    }

    activate(targetId) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.tabTarget === targetId;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        const isActive = panel.dataset.tabPanel === targetId;
        panel.classList.toggle('is-active', isActive);
      });

      // Reset slider position when switching tabs
      requestAnimationFrame(() => {
        const activePanel = this.section.querySelector(`[data-tab-panel="${targetId}"]`);
        if (activePanel) {
          const slider = activePanel.querySelector('[data-slider]');
          if (slider && slider._incipitSlider) {
            slider._incipitSlider.update();
            slider._incipitSlider.goTo(0, false);
          }
        }
      });
    }
  }


  /* ---------- SLIDER ------------------------------------------------------- */
  /**
   * Slider basé sur du scroll natif (overflow-x + scroll-snap).
   * - Mobile : swipe au doigt natif, inertie iOS/Android propre
   * - Desktop : trackpad/molette horizontale fonctionne, les flèches scrollent
   *             le track d'environ une "page" (90% de la largeur visible)
   * - Plus de drag JS, plus de transform — le navigateur gère tout
   */
  class IncipitFPSlider {
    constructor(slider) {
      this.slider = slider;
      this.track = slider.querySelector('[data-slider-track]');
      this.cards = this.track ? Array.from(this.track.children) : [];
      this.section = slider.closest('.incipit-fp');
      this.sectionId = this.section ? this.section.dataset.sectionId : null;

      slider._incipitSlider = this;

      this.bind();

      requestAnimationFrame(() => {
        this.update();
      });
    }

    bind() {
      // Update arrows on scroll (passive pour ne pas bloquer le scroll natif)
      this._onScroll = () => this.updateArrows();
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      // Update on resize
      this._onResize = debounce(() => this.update(), 120);
      window.addEventListener('resize', this._onResize);

      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObs = new ResizeObserver(this._onResize);
        this._resizeObs.observe(this.slider);
      }

      // Empêcher le drag des images natif (sinon Firefox/Safari déclenchent
      // un drag d'image au lieu de laisser le scroll horizontal s'exécuter)
      this.track.querySelectorAll('img').forEach((img) => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });

      // Boutons flèches
      if (this.section) {
        const prevBtns = this.section.querySelectorAll(`[data-slider-prev][data-section="${this.sectionId}"]`);
        const nextBtns = this.section.querySelectorAll(`[data-slider-next][data-section="${this.sectionId}"]`);
        prevBtns.forEach((b) => b.addEventListener('click', () => this.prev()));
        nextBtns.forEach((b) => b.addEventListener('click', () => this.next()));
      }

      // Update arrows after images load (la scrollWidth peut changer)
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', () => this.update(), { once: true });
        }
      });
    }

    update() {
      if (!this.cards || this.cards.length === 0) return;
      this.updateArrows();
    }

    /**
     * Va à la card d'index donné. Utilisé notamment au changement d'onglet
     * pour revenir au début du slider.
     */
    goTo(index, animate = true) {
      if (!this.cards.length) return;
      const target = this.cards[Math.max(0, Math.min(index, this.cards.length - 1))];
      if (!target) return;

      const targetLeft = target.offsetLeft - this.track.offsetLeft;

      if (animate) {
        this.track.scrollTo({ left: targetLeft, behavior: 'smooth' });
      } else {
        // Sans animation : on désactive temporairement le scroll-behavior
        const prev = this.track.style.scrollBehavior;
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = targetLeft;
        // Force reflow puis restore
        void this.track.offsetWidth;
        this.track.style.scrollBehavior = prev;
      }
    }

    /**
     * Avance d'environ une "page" (90% de la largeur visible du track).
     * Le scroll-snap s'occupe d'aligner sur le bord d'une carte.
     */
    next() {
      this.track.scrollBy({
        left: this.track.clientWidth * 0.9,
        behavior: 'smooth',
      });
    }

    prev() {
      this.track.scrollBy({
        left: -this.track.clientWidth * 0.9,
        behavior: 'smooth',
      });
    }

    updateArrows() {
      if (!this.section) return;
      const prevBtns = this.section.querySelectorAll(`[data-slider-prev][data-section="${this.sectionId}"]`);
      const nextBtns = this.section.querySelectorAll(`[data-slider-next][data-section="${this.sectionId}"]`);

      // Tolérance de 2px pour les arrondis sub-pixel
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;

      prevBtns.forEach((b) => { b.disabled = atStart; });
      nextBtns.forEach((b) => { b.disabled = atEnd; });
    }
  }


  /* ---------- ATELIER BRIDGE ----------------------------------------------- */

  /**
   * Dispatches a cart:update event in Atelier's expected format.
   * Atelier's <cart-drawer-component> with [auto-open] listens to this and opens itself.
   */
  function dispatchCartUpdate(cartData, sourceId, opts = {}) {
    const event = new CustomEvent(ATELIER_CART_EVENT, {
      bubbles: true,
      detail: {
        resource: cartData,
        sourceId: sourceId,
        data: {
          source: 'incipit-featured-products',
          itemCount: opts.itemCount || 1,
          variantId: opts.variantId,
          productId: opts.productId,
          sections: opts.sections,
        },
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Add a variant to cart, then dispatch event so Atelier cart-drawer opens.
   * @param {string|number} variantId
   * @param {object} extra — productId, sourceId, etc.
   * @returns {Promise<{ok: true, cart} | {ok: false, message}>}
   */
  async function addToCartAndOpenDrawer(variantId, extra = {}) {
    const sectionsToFetch = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      sectionsToFetch.push(el.dataset.sectionId);
    });

    try {
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');
      if (sectionsToFetch.length > 0) {
        formData.append('sections', sectionsToFetch.join(','));
      }

      const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!addResponse.ok) {
        const err = await addResponse.json().catch(() => ({}));
        return { ok: false, message: err.description || err.message || 'Erreur lors de l\'ajout' };
      }

      const addData = await addResponse.json();

      // Fetch fresh cart to get item_count
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      // Dispatch event → Atelier cart-drawer auto-opens
      dispatchCartUpdate(cartData, extra.sourceId || 'incipit-fp', {
        itemCount: cartData.item_count,
        variantId: variantId,
        productId: extra.productId,
        sections: addData.sections,
      });

      return { ok: true, cart: cartData };
    } catch (err) {
      console.error('[Incipit FP] addToCart failed', err);
      return { ok: false, message: err.message || 'Une erreur est survenue' };
    }
  }

  /**
   * Silently add to cart (no event dispatch, no fresh cart fetch).
   * Used by "Buy Now" before redirecting to checkout — no need to refresh
   * any UI since we're leaving the page anyway.
   * @param {string|number} variantId
   * @returns {Promise<{ok: true} | {ok: false, message}>}
   */
  async function silentAddToCart(variantId) {
    try {
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');

      const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!addResponse.ok) {
        const err = await addResponse.json().catch(() => ({}));
        return { ok: false, message: err.description || err.message || 'Erreur lors de l\'ajout' };
      }

      return { ok: true };
    } catch (err) {
      console.error('[Incipit FP] silentAddToCart failed', err);
      return { ok: false, message: err.message || 'Une erreur est survenue' };
    }
  }


  /* ---------- INCIPIT QUICK VIEW DRAWER (multi-variants) ------------------ */

  // Module-level cache for product fetches — avoids refetching the same product
  // if the user opens it multiple times in the same session
  const productCache = new Map();

  async function fetchProduct(handle) {
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }
    const response = await fetch('/products/' + handle + '.js');
    if (!response.ok) throw new Error('Failed to fetch product');
    const product = await response.json();
    productCache.set(handle, product);
    return product;
  }

  class IncipitQuickView {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('.incipit-qv__panel');

      // Gallery refs
      this.galleryEl = root.querySelector('[data-qv-gallery]');
      this.galleryTrack = root.querySelector('[data-qv-gallery-track]');
      this.galleryDots = root.querySelector('[data-qv-gallery-dots]');
      this.galleryPrev = root.querySelector('[data-qv-gallery-prev]');
      this.galleryNext = root.querySelector('[data-qv-gallery-next]');

      this.titleEl = root.querySelector('[data-qv-title]');
      this.eyebrowEl = root.querySelector('[data-qv-eyebrow]');
      this.priceEl = root.querySelector('[data-qv-price]');
      this.optionsEl = root.querySelector('[data-qv-options]');
      this.submitBtn = root.querySelector('[data-qv-submit]');
      this.submitLabel = root.querySelector('[data-qv-submit-label]');
      this.feedbackEl = root.querySelector('[data-qv-feedback]');
      this.viewProductLink = root.querySelector('[data-qv-view-product]');
      this.descriptionEl = root.querySelector('[data-qv-description]');
      this.buyNowBtn = root.querySelector('[data-qv-buy-now]');
      this.buyNowLabel = root.querySelector('[data-qv-buy-now-label]');
      this.badgeEl = root.querySelector('[data-qv-badge]');

      this.product = null;
      this.variants = [];
      this.options = [];
      this.selectedOptions = []; // values aligned with options

      // Gallery state
      this.galleryImages = [];
      this.galleryIndex = 0;

      // Settings from data attributes (configurable from theme editor)
      this.settings = {
        showArrows: root.dataset.showArrows !== 'false',
        showDots: root.dataset.showDots !== 'false',
        showDescription: root.dataset.showDescription !== 'false',
        descriptionLength: parseInt(root.dataset.descriptionLength, 10) || 120,
        badgeEnabled: root.dataset.badgeEnabled === 'true',
        badgeTag: (root.dataset.badgeTag || '').toLowerCase().trim(),
        badgeLabel: root.dataset.badgeLabel || '',
        badgeStyle: root.dataset.badgeStyle || 'outline',
      };

      // Swipe state (mobile only)
      this.swipeActive = false;
      this.swipeStartX = 0;
      this.swipeStartY = 0;
      this.swipeOffset = 0;
      this.swipeAxis = null;

      this.bind();
    }

    bind() {
      this.root.querySelectorAll('[data-qv-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', () => this.handleSubmit());
      }

      if (this.buyNowBtn) {
        this.buyNowBtn.addEventListener('click', () => this.handleBuyNow());
      }

      // Gallery navigation
      if (this.galleryPrev) {
        this.galleryPrev.addEventListener('click', () => this.galleryGoTo(this.galleryIndex - 1));
      }
      if (this.galleryNext) {
        this.galleryNext.addEventListener('click', () => this.galleryGoTo(this.galleryIndex + 1));
      }

      // Single consolidated keyboard handler — Escape + gallery arrows
      document.addEventListener('keydown', (e) => {
        if (!this.isOpen()) return;
        if (e.key === 'Escape') {
          this.close();
        } else if (this.galleryImages.length > 1) {
          if (e.key === 'ArrowLeft') this.galleryGoTo(this.galleryIndex - 1);
          else if (e.key === 'ArrowRight') this.galleryGoTo(this.galleryIndex + 1);
        }
      });

      // Touch swipe on gallery (mobile only) — uses Touch events to differentiate from mouse
      if (this.galleryEl) {
        this.galleryEl.addEventListener('touchstart', (e) => this.onSwipeStart(e), { passive: true });
        this.galleryEl.addEventListener('touchmove', (e) => this.onSwipeMove(e), { passive: true });
        this.galleryEl.addEventListener('touchend', (e) => this.onSwipeEnd(e));
        this.galleryEl.addEventListener('touchcancel', (e) => this.onSwipeEnd(e));
      }

      // Event delegation for gallery dots — one listener on container,
      // works regardless of how many dots are dynamically rendered
      if (this.galleryDots) {
        this.galleryDots.addEventListener('click', (e) => {
          const dot = e.target.closest('[data-qv-dot]');
          if (!dot) return;
          const idx = parseInt(dot.dataset.qvDot, 10);
          if (!isNaN(idx)) this.galleryGoTo(idx);
        });
      }

      // Event delegation for option value buttons — one listener on container
      if (this.optionsEl) {
        this.optionsEl.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-option-value]');
          if (!btn || btn.disabled) return;
          const idx = parseInt(btn.dataset.optionIndex, 10);
          if (isNaN(idx)) return;
          this.selectedOptions[idx] = btn.dataset.value;
          this.render();
        });
      }
    }

    /* ----- Touch swipe (mobile gallery) ----- */

    onSwipeStart(e) {
      if (this.galleryImages.length <= 1) return;
      const touch = e.touches[0];
      this.swipeActive = true;
      this.swipeStartX = touch.clientX;
      this.swipeStartY = touch.clientY;
      this.swipeOffset = 0;
      this.swipeAxis = null;
    }

    onSwipeMove(e) {
      if (!this.swipeActive) return;
      const touch = e.touches[0];
      const dx = touch.clientX - this.swipeStartX;
      const dy = touch.clientY - this.swipeStartY;

      // Determine axis lock once after small threshold
      if (!this.swipeAxis) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          this.swipeAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (this.swipeAxis === 'x') {
            this.galleryEl.classList.add('is-swiping');
            this.galleryTrack.classList.add('is-swiping');
          }
        }
      }

      if (this.swipeAxis !== 'x') return;

      this.swipeOffset = dx;
      // Apply transform with offset
      const baseTranslate = -100 * this.galleryIndex;
      const offsetPercent = (dx / this.galleryEl.offsetWidth) * 100;
      this.galleryTrack.style.transform = 'translate3d(' + (baseTranslate + offsetPercent) + '%, 0, 0)';
    }

    onSwipeEnd() {
      if (!this.swipeActive) return;
      const wasSwipingX = this.swipeAxis === 'x';
      this.swipeActive = false;
      this.galleryEl.classList.remove('is-swiping');
      this.galleryTrack.classList.remove('is-swiping');

      if (!wasSwipingX) {
        this.swipeAxis = null;
        return;
      }

      const distance = this.swipeOffset;
      const threshold = Math.min(60, this.galleryEl.offsetWidth * 0.15);

      if (Math.abs(distance) > threshold) {
        if (distance < 0) this.galleryGoTo(this.galleryIndex + 1);
        else this.galleryGoTo(this.galleryIndex - 1);
      } else {
        // Snap back
        this.galleryGoTo(this.galleryIndex);
      }

      this.swipeOffset = 0;
      this.swipeAxis = null;
    }

    isOpen() {
      return !this.root.hasAttribute('hidden');
    }

    /**
     * Opens drawer and fetches product data via /products/{handle}.js
     */
    async open(triggerBtn) {
      const handle = triggerBtn.dataset.productHandle;
      const productUrl = triggerBtn.dataset.productUrl || ('/products/' + handle);
      if (!handle) return;

      // Show drawer immediately with skeleton state
      this.showLoading(productUrl);

      try {
        const product = await fetchProduct(handle);

        this.product = product;
        this.variants = product.variants || [];

        // Normalize options : the Shopify API can return them as either:
        //   - array of strings: ["Size", "Color"]  (some endpoints)
        //   - array of objects: [{name: "Size", position: 1, values: [...]}]  (others)
        // We always extract the .name string and (re)compute values from variants.
        this.options = (product.options || []).map((opt, idx) => {
          const name = (typeof opt === 'string') ? opt : (opt && opt.name) ? opt.name : ('Option ' + (idx + 1));
          const values = [];
          this.variants.forEach((v) => {
            const val = v.options[idx];
            if (val && !values.includes(val)) values.push(val);
          });
          return { name: name, position: idx + 1, values };
        });

        // Initial selection : first available variant's options
        const firstAvailable = this.variants.find((v) => v.available) || this.variants[0];
        this.selectedOptions = firstAvailable ? [...firstAvailable.options] : [];

        this.render();
      } catch (err) {
        console.error('[Incipit QV] Failed to load product', err);
        this.feedbackEl.classList.add('is-error');
        this.feedbackEl.textContent = 'Impossible de charger le produit. Voir la page produit.';
      }
    }

    showLoading(productUrl) {
      // Reset content
      this.eyebrowEl.textContent = '';
      this.titleEl.textContent = 'Chargement…';
      this.priceEl.innerHTML = '';
      this.optionsEl.innerHTML = '';
      this.galleryTrack.innerHTML = '';
      this.galleryDots.innerHTML = '';
      this.galleryImages = [];
      this.galleryIndex = 0;
      this._lastVariantImageId = undefined;
      if (this.galleryPrev) this.galleryPrev.hidden = true;
      if (this.galleryNext) this.galleryNext.hidden = true;
      if (this.descriptionEl) {
        this.descriptionEl.innerHTML = '';
        this.descriptionEl.hidden = true;
      }
      if (this.badgeEl) {
        this.badgeEl.textContent = '';
        this.badgeEl.hidden = true;
      }
      this.feedbackEl.textContent = '';
      this.feedbackEl.className = 'incipit-qv__feedback';
      this.submitBtn.disabled = true;
      this.submitLabel.textContent = 'Choisir une option';
      if (this.buyNowBtn) {
        this.buyNowBtn.disabled = true;
        this.buyNowBtn.classList.remove('is-loading');
        if (this.buyNowLabel) this.buyNowLabel.textContent = 'Acheter maintenant';
      }
      if (this.viewProductLink) this.viewProductLink.href = productUrl;

      this.root.removeAttribute('hidden');
      this.root.setAttribute('aria-hidden', 'false');
      void this.root.offsetWidth;
      this.root.classList.add('is-open');
      document.body.classList.add('incipit-qv-locked');
    }

    close() {
      this.root.classList.remove('is-open');
      document.body.classList.remove('incipit-qv-locked');
      // Wait for transition end
      setTimeout(() => {
        this.root.setAttribute('hidden', '');
        this.root.setAttribute('aria-hidden', 'true');
      }, 600);
    }

    findCurrentVariant() {
      if (!this.variants.length) return null;
      return this.variants.find((v) => {
        return v.options.every((opt, i) => opt === this.selectedOptions[i]);
      }) || null;
    }

    /**
     * Returns true if a variant exists & is available with the given option value
     * for `optionIndex`, given current selections for other options.
     */
    isOptionAvailable(optionIndex, value) {
      return this.variants.some((v) => {
        if (!v.available) return false;
        if (v.options[optionIndex] !== value) return false;
        return v.options.every((opt, i) => i === optionIndex || opt === this.selectedOptions[i]);
      });
    }

    render() {
      const product = this.product;
      const currentVariant = this.findCurrentVariant();

      // Eyebrow = vendor (if not the same as title)
      const eyebrow = product.vendor && product.vendor !== product.title ? product.vendor : '';
      this.eyebrowEl.textContent = eyebrow;

      // Title
      this.titleEl.textContent = product.title;

      // Price (using current variant)
      this.renderPrice(currentVariant);

      // Description teaser (configurable via settings)
      this.renderDescription();

      // Product badge (BEST SELLER, etc. — based on product tags)
      this.renderBadge();

      // Media — only re-render gallery if the variant featured image has changed
      const newImageId = (currentVariant && currentVariant.featured_image && currentVariant.featured_image.id) || null;
      if (newImageId !== this._lastVariantImageId) {
        this._lastVariantImageId = newImageId;
        this.renderGallery(currentVariant);
      }

      // Options
      this.renderOptions();

      // Submit button state
      this.renderSubmit(currentVariant);

      // View product link
      if (this.viewProductLink && product.url) {
        this.viewProductLink.href = currentVariant
          ? product.url + '?variant=' + currentVariant.id
          : product.url;
      }
    }

    renderGallery(variant) {
      // Build the image list :
      //   1. Variant featured_image first (if exists)
      //   2. Then all product images (skipping the variant's image to avoid duplicate)
      const images = [];
      const seen = new Set();

      const addImage = (src, alt) => {
        if (!src) return;
        const normalized = (typeof src === 'string') ? src : (src.src || '');
        if (!normalized || seen.has(normalized)) return;
        seen.add(normalized);
        // Add width param for performance
        const finalSrc = normalized.includes('width=') ? normalized : (normalized + (normalized.includes('?') ? '&' : '?') + 'width=900');
        images.push({ src: finalSrc, alt: alt || this.product.title });
      };

      // Variant image first
      if (variant && variant.featured_image) {
        addImage(variant.featured_image, variant.featured_image.alt);
      }

      // Then all product images
      if (this.product.images && Array.isArray(this.product.images)) {
        this.product.images.forEach((img) => {
          addImage(img, this.product.title);
        });
      } else if (this.product.featured_image) {
        addImage(this.product.featured_image, this.product.title);
      }

      this.galleryImages = images;

      // Render slides
      this.galleryTrack.innerHTML = images.map((img) => {
        return '<div class="incipit-qv__gallery-slide">' +
                 '<img src="' + escapeHtml(img.src) + '" alt="' + escapeHtml(img.alt) + '" loading="lazy">' +
               '</div>';
      }).join('');

      // Render dots only if more than 1 image AND setting enabled
      if (images.length > 1 && this.settings.showDots) {
        this.galleryDots.innerHTML = images.map((_, i) => {
          return '<button type="button" class="incipit-qv__gallery-dot' + (i === 0 ? ' is-active' : '') +
                 '" data-qv-dot="' + i + '" aria-label="Image ' + (i + 1) + '"></button>';
        }).join('');
        // Note: click handling via event delegation in bind() — no need to re-bind here
      } else {
        this.galleryDots.innerHTML = '';
      }

      // Show arrows only if more than 1 image AND setting enabled
      const showArrows = images.length > 1 && this.settings.showArrows;
      if (this.galleryPrev) this.galleryPrev.hidden = !showArrows;
      if (this.galleryNext) this.galleryNext.hidden = !showArrows;

      // Reset to first image
      this.galleryGoTo(0, false);
    }

    galleryGoTo(index, animate = true) {
      if (this.galleryImages.length === 0) return;

      // Wrap around
      if (index < 0) index = this.galleryImages.length - 1;
      if (index >= this.galleryImages.length) index = 0;

      this.galleryIndex = index;

      if (!animate) {
        this.galleryTrack.style.transition = 'none';
      }
      this.galleryTrack.style.transform = 'translate3d(' + (-100 * index) + '%, 0, 0)';
      if (!animate) {
        // Force reflow then re-enable transition
        void this.galleryTrack.offsetWidth;
        this.galleryTrack.style.transition = '';
      }

      // Update dots
      this.galleryDots.querySelectorAll('[data-qv-dot]').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    renderDescription() {
      if (!this.descriptionEl) return;

      // Setting off → hide
      if (!this.settings.showDescription) {
        this.descriptionEl.innerHTML = '';
        this.descriptionEl.hidden = true;
        return;
      }

      const raw = this.product && this.product.description;
      if (!raw) {
        this.descriptionEl.innerHTML = '';
        this.descriptionEl.hidden = true;
        return;
      }

      // Strip HTML using DOMParser (safe, no eval)
      const tmp = document.createElement('div');
      tmp.innerHTML = raw;
      let text = (tmp.textContent || tmp.innerText || '').trim();

      // Collapse whitespace
      text = text.replace(/\s+/g, ' ');

      if (!text) {
        this.descriptionEl.innerHTML = '';
        this.descriptionEl.hidden = true;
        return;
      }

      const maxLen = this.settings.descriptionLength;

      // If shorter than max → display as-is, no ellipsis
      if (text.length <= maxLen) {
        this.descriptionEl.innerHTML = '<span class="incipit-qv__description-text">' + escapeHtml(text) + '</span>';
        this.descriptionEl.hidden = false;
        return;
      }

      // Truncate at last word boundary before maxLen
      let truncated = text.slice(0, maxLen);
      const lastSpace = truncated.lastIndexOf(' ');
      if (lastSpace > maxLen * 0.6) {
        // Only break at word if the last space is not too far back
        truncated = truncated.slice(0, lastSpace);
      }
      // Trim trailing punctuation that would clash with the ellipsis
      truncated = truncated.replace(/[.,;:!?\-—–\s]+$/, '');

      this.descriptionEl.innerHTML =
        '<span class="incipit-qv__description-text">' + escapeHtml(truncated) + '</span>' +
        '<span class="incipit-qv__description-ellipsis" aria-hidden="true">…</span>';
      this.descriptionEl.hidden = false;
    }

    /**
     * Render product badge (BEST SELLER, NOUVEAU, etc.) based on product tags.
     * - Setting must be enabled, tag and label must be set
     * - Product must have the tag (case-insensitive)
     * - Otherwise the element stays hidden, no visual gap
     */
    renderBadge() {
      if (!this.badgeEl) return;

      const settings = this.settings;
      // Setting off, or missing tag/label → hide
      if (!settings.badgeEnabled || !settings.badgeTag || !settings.badgeLabel) {
        this.badgeEl.textContent = '';
        this.badgeEl.hidden = true;
        return;
      }

      // Check if product has the tag (case-insensitive)
      const tags = (this.product && this.product.tags) || [];
      const cleanTag = settings.badgeTag.toLowerCase().trim();
      const hasTag = tags.some((t) => String(t).toLowerCase().trim() === cleanTag);

      if (!hasTag) {
        this.badgeEl.textContent = '';
        this.badgeEl.hidden = true;
        return;
      }

      // Set style modifier (filled / outline) so CSS can target it
      this.badgeEl.dataset.badgeStyle = settings.badgeStyle;
      this.badgeEl.textContent = settings.badgeLabel;
      this.badgeEl.hidden = false;
    }

    renderPrice(variant) {
      if (!variant) {
        this.priceEl.innerHTML = '';
        return;
      }
      const price = variant.price;
      const compareAt = variant.compare_at_price;

      if (compareAt && compareAt > price) {
        this.priceEl.innerHTML =
          '<s class="incipit-qv__price-compare">' + escapeHtml(formatMoney(compareAt)) + '</s>' +
          '<span class="incipit-qv__price-current incipit-qv__price-current--sale">' + escapeHtml(formatMoney(price)) + '</span>';
      } else {
        this.priceEl.innerHTML =
          '<span class="incipit-qv__price-current">' + escapeHtml(formatMoney(price)) + '</span>';
      }
    }

    renderOptions() {
      // Hide options block entirely if only one variant
      if (this.variants.length <= 1) {
        this.optionsEl.innerHTML = '';
        return;
      }

      let html = '';
      this.options.forEach((option, optIdx) => {
        if (!option.values || option.values.length <= 1) return;
        const selectedValue = this.selectedOptions[optIdx] || '';

        html += '<div class="incipit-qv__option" data-option-index="' + optIdx + '">';
        html +=   '<div class="incipit-qv__option-label">';
        html +=     '<span class="incipit-qv__option-label-name">' + escapeHtml(option.name) + '</span>';
        if (selectedValue) {
          html +=   '<span class="incipit-qv__option-label-selected">' + escapeHtml(selectedValue) + '</span>';
        }
        html +=   '</div>';
        html +=   '<div class="incipit-qv__option-values">';
        option.values.forEach((value) => {
          const isSelected = selectedValue === value;
          const isAvailable = this.isOptionAvailable(optIdx, value);
          html += '<button type="button"' +
            ' class="incipit-qv__option-value' + (isSelected ? ' is-selected' : '') + '"' +
            ' data-option-value' +
            ' data-option-index="' + optIdx + '"' +
            ' data-value="' + escapeHtml(value) + '"' +
            (!isAvailable ? ' disabled' : '') +
            '>' + escapeHtml(value) + '</button>';
        });
        html +=   '</div>';
        html += '</div>';
      });

      this.optionsEl.innerHTML = html;
      // Note: click handling via event delegation in bind() — no need to re-bind here
    }

    renderSubmit(variant) {
      this.feedbackEl.textContent = '';
      this.feedbackEl.className = 'incipit-qv__feedback';

      if (!variant) {
        this.submitBtn.disabled = true;
        this.submitLabel.textContent = 'Choisir une option';
        if (this.buyNowBtn) {
          this.buyNowBtn.disabled = true;
          if (this.buyNowLabel) this.buyNowLabel.textContent = 'Acheter maintenant';
        }
        return;
      }
      if (!variant.available) {
        this.submitBtn.disabled = true;
        this.submitLabel.textContent = 'Épuisé';
        if (this.buyNowBtn) {
          this.buyNowBtn.disabled = true;
          if (this.buyNowLabel) this.buyNowLabel.textContent = 'Indisponible';
        }
        return;
      }
      this.submitBtn.disabled = false;
      this.submitLabel.textContent = 'Ajouter au panier';
      if (this.buyNowBtn) {
        this.buyNowBtn.disabled = false;
        if (this.buyNowLabel) this.buyNowLabel.textContent = 'Acheter maintenant';
      }
    }

    async handleSubmit() {
      const variant = this.findCurrentVariant();
      if (!variant || !variant.available) return;

      this.submitBtn.classList.add('is-loading');
      this.submitBtn.disabled = true;
      if (this.buyNowBtn) this.buyNowBtn.disabled = true;
      this.feedbackEl.textContent = '';
      this.feedbackEl.className = 'incipit-qv__feedback';

      const result = await addToCartAndOpenDrawer(variant.id, {
        productId: this.product.id,
        sourceId: 'incipit-qv',
      });

      this.submitBtn.classList.remove('is-loading');

      if (result.ok) {
        // Close our drawer — Atelier cart-drawer will open automatically via the event we dispatched
        this.close();
      } else {
        this.submitBtn.disabled = false;
        if (this.buyNowBtn) this.buyNowBtn.disabled = false;
        this.feedbackEl.classList.add('is-error');
        this.feedbackEl.textContent = result.message || 'Une erreur est survenue';
      }
    }

    async handleBuyNow() {
      const variant = this.findCurrentVariant();
      if (!variant || !variant.available) return;

      this.buyNowBtn.classList.add('is-loading');
      this.buyNowBtn.disabled = true;
      this.submitBtn.disabled = true;
      this.feedbackEl.textContent = '';
      this.feedbackEl.className = 'incipit-qv__feedback';

      // Silent add to cart (no event dispatch — we're leaving the page)
      const result = await silentAddToCart(variant.id);

      if (result.ok) {
        // Redirect to checkout immediately
        const checkoutUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url)
          ? '/checkout'
          : '/checkout';
        window.location.href = checkoutUrl;
      } else {
        this.buyNowBtn.classList.remove('is-loading');
        this.buyNowBtn.disabled = false;
        this.submitBtn.disabled = false;
        this.feedbackEl.classList.add('is-error');
        this.feedbackEl.textContent = result.message || 'Une erreur est survenue';
      }
    }
  }


  /* ---------- QUICK ADD button — direct or open Incipit Quick View -------- */

  let quickViewInstance = null;

  function getOrCreateQuickView() {
    if (quickViewInstance) return quickViewInstance;
    const root = document.querySelector('incipit-quick-view');
    if (!root) return null;
    quickViewInstance = new IncipitQuickView(root);
    return quickViewInstance;
  }

  async function quickAddDirect(btn) {
    const variantId = btn.dataset.defaultVariant;
    if (!variantId) return;

    btn.classList.add('is-loading');

    const result = await addToCartAndOpenDrawer(variantId, {
      productId: btn.dataset.productId,
      sourceId: btn.id || 'incipit-fp',
    });

    btn.classList.remove('is-loading');

    if (result.ok) {
      btn.classList.add('is-success');
      setTimeout(() => btn.classList.remove('is-success'), 1400);
    } else {
      btn.style.borderColor = '#b04545';
      setTimeout(() => { btn.style.borderColor = ''; }, 1500);
    }
  }


  /* ---------- REVEAL ANIMATIONS ------------------------------------------ */

  /**
   * Reveal-on-scroll : fade + light translate when element enters viewport.
   * - [data-reveal] elements : use their own data-reveal-delay (in ms)
   * - [data-reveal-card] elements : auto-stagger (80ms between each)
   * - One-shot : we stop observing once revealed
   * - Respects prefers-reduced-motion (CSS handles that)
   */
  function initReveal(section) {
    // If IntersectionObserver isn't available, reveal everything immediately
    if (typeof IntersectionObserver === 'undefined') {
      section.querySelectorAll('[data-reveal], [data-reveal-card]').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }

    // Honor user's motion preference — reveal immediately, no animation
    const prefersReducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      section.querySelectorAll('[data-reveal], [data-reveal-card]').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const el = entry.target;
        let delay = 0;

        if (el.hasAttribute('data-reveal-card')) {
          // Auto-stagger : compute delay based on card index in the same track
          const track = el.closest('[data-slider-track]');
          if (track) {
            const cards = Array.from(track.querySelectorAll('[data-reveal-card]'));
            const idx = cards.indexOf(el);
            // Base 400ms (after head/controls) + 80ms per card
            delay = 400 + idx * 80;
          }
        } else {
          delay = parseInt(el.dataset.revealDelay, 10) || 0;
        }

        setTimeout(() => el.classList.add('is-revealed'), delay);
        observer.unobserve(el);
      });
    }, {
      // Trigger when element is ~85% in viewport (rootMargin pulls the trigger up)
      threshold: 0.05,
      rootMargin: '0px 0px -10% 0px',
    });

    section.querySelectorAll('[data-reveal], [data-reveal-card]').forEach((el) => {
      observer.observe(el);
    });
  }


  /* ---------- INIT --------------------------------------------------------- */

  function initSection(section) {
    if (!section || section.dataset.incipitInit === 'true') return;
    section.dataset.incipitInit = 'true';

    new IncipitFPTabs(section);

    section.querySelectorAll('[data-slider]').forEach((slider) => {
      new IncipitFPSlider(slider);
    });

    initReveal(section);

    section.querySelectorAll('[data-quick-add]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (btn.classList.contains('is-loading')) return;

        const hasVariants = btn.dataset.hasVariants === 'true';
        if (hasVariants) {
          const qv = getOrCreateQuickView();
          if (qv) {
            qv.open(btn);
          } else {
            // Fallback : redirect if quick view not present in DOM
            window.location.href = btn.dataset.productUrl || '/products/' + btn.dataset.productHandle;
          }
        } else {
          quickAddDirect(btn);
        }
      });

      // Prefetch product data on hover/focus — drawer feels instant on click
      // Only for products with variants (the ones that need a fetch)
      if (btn.dataset.hasVariants === 'true') {
        let prefetchTimer = null;
        const triggerPrefetch = () => {
          const handle = btn.dataset.productHandle;
          if (!handle || productCache.has(handle)) return;
          // Small delay to avoid prefetching on rapid mouse movements
          prefetchTimer = setTimeout(() => {
            fetchProduct(handle).catch(() => { /* silent fail — will retry on click */ });
          }, 100);
        };
        const cancelPrefetch = () => {
          if (prefetchTimer) {
            clearTimeout(prefetchTimer);
            prefetchTimer = null;
          }
        };
        btn.addEventListener('mouseenter', triggerPrefetch);
        btn.addEventListener('focus', triggerPrefetch);
        btn.addEventListener('mouseleave', cancelPrefetch);
        btn.addEventListener('blur', cancelPrefetch);
      }
    });
  }

  function initAll() {
    document.querySelectorAll('.incipit-fp').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.incipit-fp');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:reorder', initAll);
})();